import { buildLeadSourceVehicleReference } from './leads/build-lead-source-vehicle-reference.js';
import './leads/Lead.js';
import { buildLeadAddress } from './leads/lead-address.utils.js';
import { isMarketingAttribution } from './MarketingAttribution.js';
import { LeadSourceType, LeadType, LeadStageType } from './leads/lead-enums.js';

/**
 * Whether a cart is a probe by Ekho's own runner (see `CheckoutCart.synthetic`).
 * The one predicate every gate reads: lead derivation, the abandoned-cart
 * candidates and webhooks, the dealer lead email, and the nightly purge.
 */
function isSyntheticCart(cart) {
    return cart?.synthetic === true;
}
function toCount(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
/**
 * Whether a cart has moved past browsing: an order was placed from it or a
 * purchase was recorded on it. Such a cart is a buyer's whatever else says,
 * so it can never be marked synthetic after the fact and the purge leaves it
 * in place. Reads both spellings of the purchase counter, because a stored
 * cart carries `numPuchasesCreated` and an abandoned copy `numPurchasesCreated`.
 */
function hasPurchaseProgress(cart) {
    const purchases = Math.max(toCount(cart.numPuchasesCreated), toCount(cart.numPurchasesCreated));
    return ((typeof cart.placedOrderId === 'string' && cart.placedOrderId.length > 0) ||
        purchases > 0 ||
        typeof cart.dateofLastPurchase === 'number' ||
        typeof cart.dateOfLastPurchase === 'number');
}
const OrderCreationType = {
    ORGANIC: 'ORGANIC',
    PREORDER: 'PREORDER',
    EXISTINGORDER: 'EXISTINGORDER',
    OEMORIGINATED: 'OEMORIGINATED',
    ABANDONEDCART: 'ABANDONEDCART',
    ANTE_PREORDER: 'ANTE_PREORDER',
    ABANDONED_ANTE_PREORDER: 'ABANDONED_ANTE_PREORDER',
    DEALER_LEAD: 'DEALER_LEAD',
};
const USState = {
    AL: 'AL',
    AK: 'AK',
    AZ: 'AZ',
    AR: 'AR',
    CA: 'CA',
    CO: 'CO',
    CT: 'CT',
    DE: 'DE',
    DC: 'DC',
    FL: 'FL',
    GA: 'GA',
    HI: 'HI',
    ID: 'ID',
    IL: 'IL',
    IN: 'IN',
    IA: 'IA',
    KS: 'KS',
    KY: 'KY',
    LA: 'LA',
    ME: 'ME',
    MD: 'MD',
    MA: 'MA',
    MI: 'MI',
    MN: 'MN',
    MS: 'MS',
    MO: 'MO',
    MT: 'MT',
    NE: 'NE',
    NV: 'NV',
    NH: 'NH',
    NJ: 'NJ',
    NM: 'NM',
    NY: 'NY',
    NC: 'NC',
    ND: 'ND',
    OH: 'OH',
    OK: 'OK',
    OR: 'OR',
    PA: 'PA',
    RI: 'RI',
    SC: 'SC',
    SD: 'SD',
    TN: 'TN',
    TX: 'TX',
    UT: 'UT',
    VT: 'VT',
    VA: 'VA',
    WA: 'WA',
    WV: 'WV',
    WI: 'WI',
    WY: 'WY',
};
const VehicleOrderType = {
    ANTE_PREORDER: 'ante_preorder',
    PREORDER: 'preorder',
    BUILD_TO_ORDER: 'buildToOrder',
    INVENTORY_ORDER: 'inventoryOrder',
};
const LinkUseType = {
    SINGLE_USE: 'SINGLE_USE',
    MULTI_USE: 'MULTI_USE',
};
const DiscountCategory = {
    DISCOUNT_CAMPAIGN: 'DISCOUNT_CAMPAIGN',
    CUSTOM_DISCOUNT: 'CUSTOM_DISCOUNT',
    REENGAGEMENT_DISCOUNT: 'REENGAGEMENT_DISCOUNT',
    FULL_PAYMENT_DISCOUNT: 'FULL_PAYMENT_DISCOUNT',
};
/**
 * CheckoutCartEntity - Entity class that implements ILeadSource
 * Uses interface merging to automatically inherit all CheckoutCart properties
 */
class CheckoutCartEntity {
    constructor(data) {
        Object.assign(this, data);
        this.validateRequiredProperties();
        // Initialize ILeadSource properties
        this.sourceType = this.getSourceType();
        this.sourceId = this.getId();
        this.sourceMetadata = this.extractSourceMetadata();
    }
    validateRequiredProperties() {
        const requiredProps = ['id', 'vehicles', 'dateCreated'];
        for (const prop of requiredProps) {
            if (this[prop] === undefined) {
                throw new Error(`Required property '${prop}' is missing from CheckoutCart data`);
            }
        }
        // Validate vehicles array is not empty
        if (!Array.isArray(this.vehicles) || this.vehicles.length === 0) {
            throw new Error('CheckoutCart must have at least one vehicle');
        }
    }
    shouldCreateLead() {
        // A synthetic cart is a probe by Ekho's own runner, never a buyer.
        if (isSyntheticCart(this))
            return false;
        // Check if orderCreationType indicates abandonment or buy-in-person intent
        // ABANDONEDCART, ABANDONED_ANTE_PREORDER, DEALER_LEAD qualify
        const leadQualifyingTypes = [
            OrderCreationType.ABANDONEDCART,
            OrderCreationType.ABANDONED_ANTE_PREORDER,
            OrderCreationType.DEALER_LEAD,
        ];
        return leadQualifyingTypes.includes(this.orderCreationType);
    }
    extractSourceMetadata() {
        return {
            dateCreated: this.dateCreated,
            cartValue: this.calculateCartTotal(),
            vehicleName: this.getPrimaryVehicleName(),
            status: this.orderCreationType || 'UNKNOWN',
        };
    }
    extractLeadData(buyer, programOwner) {
        const marketingAttribution = isMarketingAttribution(this.marketingAttribution)
            ? this.marketingAttribution
            : undefined;
        const address = buildLeadAddress(buyer);
        const vehicleReferences = this.buildVehicleReferences();
        return {
            email: buyer.email,
            phoneNumber: buyer.phone,
            fullName: buyer.fullName,
            ...(address ? { address } : {}),
            cartTotal: this.calculateCartTotal(),
            vehicleName: this.getPrimaryVehicleName(),
            models: this.extractVehicleModels(),
            type: this.getLeadType(),
            buyerId: this.buyerId,
            programOwnerId: programOwner.id,
            svpId: this.getSvpId(programOwner),
            marketingAttribution,
            ...(vehicleReferences.length > 0 ? { enrichment: { vehicleReferences } } : {}),
        };
    }
    /**
     * Cart vehicles as lead enrichment refs, stamped with this cart as origin.
     * Matcher `id` is not set here. Unit identity stays on `inventoryItemId` /
     * `productId` so the id-less merge fingerprint can keep distinct units
     * without seeding inventory ids into conversation hydrate.
     */
    buildVehicleReferences() {
        return (this.vehicles ?? []).map((vehicle) => buildLeadSourceVehicleReference({
            sourceType: this.sourceType,
            sourceId: this.sourceId,
            ...(vehicle.inventoryItemId ? { inventoryItemId: vehicle.inventoryItemId } : {}),
            ...(vehicle.productId ? { productId: vehicle.productId } : {}),
            ...(vehicle.model ? { model: vehicle.model } : {}),
            ...(vehicle.basePrice != null ? { advertisedPrice: vehicle.basePrice } : {}),
            ...(vehicle.name ? { displayName: vehicle.name } : {}),
            status: 'active',
            resolutionMode: vehicle.inventoryItemId ? 'specific' : 'browse',
        }));
    }
    getSourceType() {
        return LeadSourceType.CHECKOUT_CART;
    }
    getLeadType() {
        // Map orderCreationType to LeadType
        const mapping = {
            [OrderCreationType.ABANDONEDCART]: LeadType.ABANDONED_CART,
            [OrderCreationType.ABANDONED_ANTE_PREORDER]: LeadType.ABANDONED_PREORDER_CART,
            [OrderCreationType.DEALER_LEAD]: LeadType.BUY_IN_PERSON,
        };
        return mapping[this.orderCreationType] || LeadType.ABANDONED_CART;
    }
    /** The lead a rep sent this cart to. The pipeline attaches the cart there first. */
    getInvitedLeadId() {
        return this.invitedLeadId;
    }
    /**
     * The fallback for a cart a rep sent to a lead, used only when that lead
     * is gone or belongs to another dealer (see `getInvitedLeadId`). Every
     * other cart keeps identifier dedupe alone. The rep may have entered only
     * a phone, and the shopper then enters an email in checkout, so
     * `email|svpId` alone would mint a second lead. On the copy checkout-api
     * makes when the shopper enters that email, `prefilledInfo.buyerEmail` is
     * the email they entered and `prefilledInfo.buyerPhone` is the phone the
     * rep sent the link to.
     */
    extractContactMatchCandidates() {
        if (!this.invitedLeadId)
            return {};
        const prefilled = this.customLinkConfig?.prefilledInfo;
        return {
            ...(prefilled?.buyerEmail ? { email: prefilled.buyerEmail } : {}),
            ...(prefilled?.buyerPhone ? { phone: prefilled.buyerPhone } : {}),
        };
    }
    extractIdentifierComponents(buyer, programOwner) {
        return {
            identifier: buyer.email,
            svpId: this.getSvpId(programOwner),
        };
    }
    extractVehicleModels() {
        // Extract all vehicle model names from cart vehicles
        return (this.vehicles
            ?.map((vehicle) => vehicle.name || vehicle.model)
            .filter((name) => Boolean(name)) || []);
    }
    getBuyerId() {
        return this.buyerId;
    }
    getId() {
        return this.id;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- parameter required by interface contract
    getSvpId(programOwner) {
        return this.svpId;
    }
    getUniqueSourceIdentifier() {
        return `${this.getSourceType()}|${this.getId()}`;
    }
    sourceToJson() {
        return {
            sourceType: this.sourceType,
            sourceId: this.sourceId,
            sourceMetadata: this.sourceMetadata,
        };
    }
    shouldTriggerADFNotificationOnCreate() {
        return true;
    }
    getLeadSvpIds(programOwner) {
        if (!programOwner)
            return [];
        return [this.getSvpId(programOwner) ?? ''].filter(Boolean);
    }
    calculateCartTotal() {
        // Sum vehicle basePrice + accessories prices for each vehicle
        let total = 0;
        if (this.vehicles) {
            this.vehicles.forEach((vehicle) => {
                // Add vehicle base price
                total += vehicle.basePrice || 0;
                // Add accessories prices for this vehicle
                if (vehicle.accessories) {
                    vehicle.accessories.forEach((accessory) => {
                        total += accessory.price || 0;
                    });
                }
            });
        }
        return total;
    }
    getPrimaryVehicleName() {
        // Get first vehicle name, handling both Canon and Pre-Canon structures
        const firstVehicle = this.vehicles?.[0];
        return firstVehicle?.name || firstVehicle?.model || 'Unknown Vehicle';
    }
    calculateLeadStage(previousDocument) {
        // Only trigger stage change when isDisabled changes from false to true
        if (this.isDisabled === true && previousDocument?.isDisabled === false) {
            // Different logic based on orderCreationType
            if (this.orderCreationType === OrderCreationType.ABANDONED_ANTE_PREORDER) {
                return {
                    newStage: LeadStageType.CLOSED_WON,
                    reason: 'Ante preorder cart converted to preorder',
                    source: 'CHECKOUT_CART_DISABLED',
                };
            }
            if (this.orderCreationType === OrderCreationType.ABANDONEDCART) {
                return {
                    newStage: LeadStageType.CLOSED_WON,
                    reason: 'Checkout cart conversion completed',
                    source: 'CHECKOUT_CART_DISABLED',
                };
            }
            throw new Error(`Unsupported orderCreationType ${this.orderCreationType} received - should have been filtered by shouldCreateLead upstream`);
        }
        return null; // No stage change needed
    }
    static getCollectionInfo() {
        return {
            collectionName: 'CheckoutCarts',
            isCollectionGroup: false,
        };
    }
}
/**
 * @deprecated
 */
// interface VehicleAccessory {
// 	id: string;
// 	productId: string;
// 	variantId: string;
// 	count: number;
// 	name: string;
// 	basePrice: number;
// 	totalPrice: number;
// 	specs: AccessorySpec[];
// 	imgUrl: string;
// 	description: string;
// 	boltOnVehicleIds: string[];
// 	oemId: string; // TODO remove
// 	programOwnerId: string;
// 	sku: string;
// }
// interface VehicleSpec {
// 	name: string;
// 	price: number;
// 	option: string;
// 	description?: string;
// 	id?: string;
// }
// interface AccessorySpec {
// 	name: string;
// 	price: number;
// 	option: string;
// 	description?: string;
// 	id?: string;
// }

export { CheckoutCartEntity, DiscountCategory, LinkUseType, OrderCreationType, USState, VehicleOrderType, hasPurchaseProgress, isSyntheticCart };
//# sourceMappingURL=CheckoutCart.js.map
